import React, { useState } from 'react'
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CButton,
  CFormInput,
  CBadge,
  CSmartTable,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CTooltip,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilCreditCard,
  cilCheckCircle,
  cilPeople,
  cilXCircle,
  cilPlus,
  cilCloudUpload,
  cilOptions,
  cilFilter,
  cilCloudDownload,
  cilCopy,
  cilEnvelopeClosed,
} from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'

const MOCK_CODES = [
  {
    id: 1,
    code: 'MCT-7X4K-9Z2L',
    assignedTo: 'Rahul Sharma',
    email: 'rahul.sharma@email.com',
    status: 'Joined',
    sent: '13 Jul 2026',
    joined: '13 Jul 2026',
    createdOn: '13 Jul 2026, 10:30 AM',
    expiresOn: '20 Aug 2026',
    daysLeft: 38,
  },
  {
    id: 2,
    code: 'MCT-Z8QW-1P9D',
    assignedTo: null,
    email: null,
    status: 'Active',
    sent: null,
    joined: null,
    createdOn: '13 Jul 2026, 10:28 AM',
    expiresOn: '20 Aug 2026',
    daysLeft: 38,
  },
  {
    id: 3,
    code: 'MCT-6M2N-7R3B',
    assignedTo: 'John D’Souza',
    email: 'john.dsouza@email.com',
    status: 'Joined',
    sent: '12 Jul 2026',
    joined: '12 Jul 2026',
    createdOn: '12 Jul 2026, 09:12 AM',
    expiresOn: '19 Aug 2026',
    daysLeft: 37,
  },
  {
    id: 4,
    code: 'MCT-K5PL-8H6J',
    assignedTo: null,
    email: null,
    status: 'Expired',
    sent: null,
    joined: null,
    createdOn: '10 Jul 2026, 04:45 PM',
    expiresOn: '12 Jul 2026',
    daysLeft: null,
  },
  {
    id: 5,
    code: 'MCT-U1Y2-9T7G',
    assignedTo: 'Priya Patel',
    email: 'priya.patel@email.com',
    status: 'Joined',
    sent: '10 Jul 2026',
    joined: '11 Jul 2026',
    createdOn: '10 Jul 2026, 11:02 AM',
    expiresOn: '17 Aug 2026',
    daysLeft: 35,
  },
]

const STATS = [
  {
    label: 'Total Codes',
    sub: 'All time codes generated',
    value: 245,
    icon: cilCreditCard,
    color: 'primary',
  },
  {
    label: 'Active Codes',
    sub: 'Available to use',
    value: 128,
    icon: cilCheckCircle,
    color: 'success',
  },
  { label: 'Used Codes', sub: 'Members have joined', value: 102, icon: cilPeople, color: 'info' },
  {
    label: 'Expired / Disabled',
    sub: 'No longer valid',
    value: 15,
    icon: cilXCircle,
    color: 'danger',
  },
]

const statusBadge = (status) => {
  const color = { Joined: 'success', Active: 'info', Expired: 'danger', Disabled: 'secondary' }[
    status
  ]
  return <CBadge color={color}>{status}</CBadge>
}

const notImplemented = (action) =>
  ToastComponent(`${action} — UI preview only, not yet connected to the API.`, 'info')

const columns = [
  { key: 'code', label: 'Code' },
  { key: 'assignedTo', label: 'Assigned To' },
  { key: 'status', label: 'Status' },
  { key: 'sent', label: 'Sent' },
  { key: 'joined', label: 'Joined' },
  { key: 'createdOn', label: 'Created On' },
  { key: 'expiresOn', label: 'Expires On' },
  { key: 'actions', label: 'Actions', filter: false, sorter: false },
]

const GameAccessCodes = () => {
  const [search, setSearch] = useState('')

  const items = MOCK_CODES.filter((row) => {
    if (!search) return true
    const q = search.toLowerCase()
    return row.code.toLowerCase().includes(q) || row.assignedTo?.toLowerCase().includes(q)
  })

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Game Access Codes</h4>
          <div className="text-medium-emphasis">
            Manage the unique codes members use to join this private game.
          </div>
        </div>
        <CButton color="primary" variant="outline" onClick={() => notImplemented('View Game Hub')}>
          View Game Hub
        </CButton>
      </div>

      <CRow className="g-3 mb-4">
        {STATS.map((s) => (
          <CCol sm={6} lg={3} key={s.label}>
            <CCard className="h-100">
              <CCardBody className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-medium-emphasis small">{s.label}</div>
                  <div className="fs-4 fw-semibold">{s.value}</div>
                  <div className="text-medium-emphasis small">{s.sub}</div>
                </div>
                <div
                  className={`d-flex align-items-center justify-content-center rounded-circle bg-${s.color}-subtle text-${s.color}`}
                  style={{ width: 42, height: 42 }}
                >
                  <CIcon icon={s.icon} size="lg" />
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        ))}
      </CRow>

      <CCard>
        <CCardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div className="d-flex flex-wrap gap-2">
            <CButton color="primary" onClick={() => notImplemented('Generate Single Code')}>
              <CIcon icon={cilPlus} className="me-1" />
              Generate Single Code
            </CButton>
            <CButton
              color="secondary"
              variant="outline"
              onClick={() => notImplemented('Generate Multiple Codes')}
            >
              <CIcon icon={cilPlus} className="me-1" />
              Generate Multiple Codes
            </CButton>
            <CButton
              color="secondary"
              variant="outline"
              onClick={() => notImplemented('Import Members')}
            >
              <CIcon icon={cilCloudUpload} className="me-1" />
              Import Members
            </CButton>
            <CDropdown>
              <CDropdownToggle color="secondary" variant="outline">
                <CIcon icon={cilOptions} className="me-1" />
                More Actions
              </CDropdownToggle>
              <CDropdownMenu>
                <CDropdownItem onClick={() => notImplemented('Disable selected codes')}>
                  Disable Selected
                </CDropdownItem>
                <CDropdownItem onClick={() => notImplemented('Resend invites')}>
                  <CIcon icon={cilEnvelopeClosed} className="me-2" />
                  Resend Invites
                </CDropdownItem>
                <CDropdownItem onClick={() => notImplemented('Delete selected codes')}>
                  Delete Selected
                </CDropdownItem>
              </CDropdownMenu>
            </CDropdown>
          </div>
          <div className="d-flex gap-2">
            <CFormInput
              placeholder="Search by code or member name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ minWidth: 240 }}
            />
            <CButton color="secondary" variant="outline" onClick={() => notImplemented('Filters')}>
              <CIcon icon={cilFilter} className="me-1" />
              Filters
            </CButton>
            <CButton color="secondary" variant="outline" onClick={() => notImplemented('Export')}>
              <CIcon icon={cilCloudDownload} className="me-1" />
              Export
            </CButton>
          </div>
        </CCardHeader>
        <CCardBody>
          <CSmartTable
            columns={columns}
            items={items}
            itemsPerPage={20}
            itemsPerPageSelect
            pagination
            tableProps={{ hover: true, responsive: true }}
            scopedColumns={{
              code: (item) => (
                <td>
                  <span className="fw-semibold text-primary">{item.code}</span>{' '}
                  <CIcon
                    icon={cilCopy}
                    className="text-medium-emphasis ms-1"
                    role="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(item.code)
                      ToastComponent('Code copied to clipboard.', 'success')
                    }}
                  />
                </td>
              ),
              assignedTo: (item) => (
                <td>
                  {item.assignedTo ? (
                    <>
                      <div>{item.assignedTo}</div>
                      <div className="text-medium-emphasis small">{item.email}</div>
                    </>
                  ) : (
                    <span className="text-medium-emphasis">Not used yet</span>
                  )}
                </td>
              ),
              status: (item) => <td>{statusBadge(item.status)}</td>,
              sent: (item) => <td>{item.sent ? `${item.sent} via Email` : '—'}</td>,
              joined: (item) => (
                <td className={item.joined ? 'text-success' : ''}>{item.joined ?? '—'}</td>
              ),
              expiresOn: (item) => (
                <td>
                  <div>{item.expiresOn}</div>
                  <div
                    className={`small ${item.daysLeft ? 'text-medium-emphasis' : 'text-danger'}`}
                  >
                    {item.daysLeft ? `(${item.daysLeft} days left)` : '(Expired)'}
                  </div>
                </td>
              ),
              actions: (item) => (
                <td className="text-nowrap">
                  <CTooltip content="Copy code">
                    <CIcon
                      icon={cilCopy}
                      role="button"
                      className="me-3"
                      onClick={() => {
                        navigator.clipboard?.writeText(item.code)
                        ToastComponent('Code copied to clipboard.', 'success')
                      }}
                    />
                  </CTooltip>
                  {item.status !== 'Expired' && (
                    <CTooltip content="Send via email">
                      <CIcon
                        icon={cilEnvelopeClosed}
                        role="button"
                        className="me-3"
                        onClick={() => notImplemented('Send via email')}
                      />
                    </CTooltip>
                  )}
                  <CDropdown alignment="end">
                    <CDropdownToggle color="transparent" caret={false} className="p-0">
                      <CIcon icon={cilOptions} role="button" />
                    </CDropdownToggle>
                    <CDropdownMenu>
                      <CDropdownItem onClick={() => notImplemented('Disable code')}>
                        Disable
                      </CDropdownItem>
                      <CDropdownItem onClick={() => notImplemented('Delete code')}>
                        Delete
                      </CDropdownItem>
                    </CDropdownMenu>
                  </CDropdown>
                </td>
              ),
            }}
          />
        </CCardBody>
      </CCard>
    </>
  )
}

export default GameAccessCodes
